import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Zap, 
  Users, 
  Trophy, 
  Play,
  Target,
  Shield,
  Hammer,
  Crown,
  Gamepad2
} from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { getBreadcrumbSchema, getFAQSchema, getHowToSchema, getGameSchema } from "@/utils/structuredData";
import { supabase } from "@/integrations/supabase/client";

const ComoJogarFortnite = () => {
  const navigate = useNavigate();
  const [mentorCount, setMentorCount] = useState(0);

  // Buscar número de mentores de Fortnite
  useEffect(() => {
    const fetchMentorCount = async () => {
      try {
        const { data: gameData } = await supabase
          .from("games")
          .select("id")
          .ilike("name", "%fortnite%")
          .single();

        if (gameData) {
          const { data: offerings } = await supabase
            .from("mentorship_offerings")
            .select("mentor_id")
            .eq("game_id", gameData.id)
            .eq("is_active", true);

          const uniqueMentors = new Set(offerings?.map(o => o.mentor_id) || []);
          setMentorCount(uniqueMentors.size);
        }
      } catch (error) {
        console.error("Erro ao buscar mentores:", error);
      }
    };

    fetchMentorCount();
  }, []);

  // FAQ para dados estruturados
  const faqs = [
    {
      question: "Como começar a jogar Fortnite?",
      answer: "Para começar no Fortnite, baixe o jogo gratuitamente, complete o tutorial, pratique construção no Playground e comece jogando Team Rumble para aprender sem pressão."
    },
    {
      question: "Qual é a melhor estratégia para iniciantes?",
      answer: "Para iniciantes, foque em sobreviver: aterrisse em locais menos movimentados, colete recursos, pratique construção básica e evite confrontos até se sentir confiante."
    },
    {
      question: "Como melhorar na construção?",
      answer: "Pratique diariamente no modo Criativo, aprenda builds básicos (parede-rampa-piso), treine edições rápidas e assista tutoriais de pro players."
    },
    {
      question: "Vale a pena ter um mentor de Fortnite?",
      answer: "Sim! Um mentor acelera seu aprendizado em construção, edição, posicionamento, rotações e estratégias de end-game para você melhorar rapidamente."
    }
  ];

  // Passos para dados estruturados HowTo
  const howToSteps = [
    {
      name: "Baixar e Instalar",
      text: "Baixe o Fortnite gratuitamente na Epic Games Store e complete a instalação."
    },
    {
      name: "Tutorial Completo",
      text: "Complete o tutorial para aprender controles básicos, construção e mecânicas do jogo."
    },
    {
      name: "Configurar Controles",
      text: "Ajuste sensibilidade, keybinds e configurações gráficas para sua preferência."
    },
    {
      name: "Praticar Construção",
      text: "Use o modo Criativo para praticar builds básicos e edições sem pressão."
    }
  ];

  // SEO otimizado
  useSEO({
    title: "Como Jogar Fortnite - Guia Completo 2024 para Iniciantes",
    description: "Aprenda como jogar Fortnite do zero! Guia completo com dicas de construção, mira, estratégias e como conseguir Victory Royale. Mentores profissionais disponíveis.",
    keywords: "como jogar fortnite, fortnite iniciantes, guia fortnite, dicas fortnite, mentor fortnite, coach fortnite, construção fortnite, victory royale",
    canonicalUrl: "https://arena-pro-player.com/como-jogar-fortnite",
    structuredData: [
      getBreadcrumbSchema([
        { name: "Início", url: "https://arena-pro-player.com/" },
        { name: "Jogos", url: "https://arena-pro-player.com/jogos" },
        { name: "Como Jogar Fortnite", url: "https://arena-pro-player.com/como-jogar-fortnite" }
      ]),
      getFAQSchema(faqs),
      getHowToSchema("Como Jogar Fortnite", "Guia completo para iniciantes aprenderem Fortnite", howToSteps),
      getGameSchema({ name: "Fortnite", cover_url: "/fortnite-cover.jpg" }, mentorCount)
    ]
  });

  return (
    <div className="min-h-screen bg-[#0f141c] text-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-[#0f141c] via-[#1a2332] to-[#0f141c]">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-center opacity-5"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Badge className="mb-4 bg-[#00D4FF] text-black">
              <Crown className="h-4 w-4 mr-2" />
              Fortnite
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Como Jogar <span className="text-[#00D4FF]">Fortnite</span>
              <br />
              <span className="text-2xl md:text-3xl text-gray-300">Guia Completo 2024</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Aprenda Fortnite do zero com nosso guia completo! Dicas de construção, mira, 
              estratégias e como conseguir Victory Royale com mentores profissionais.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-[#00D4FF] hover:bg-[#00B8E6] text-black"
                onClick={() => navigate("/mentor-fortnite")}
              >
                <Users className="h-5 w-5 mr-2" />
                Encontrar Mentor de Fortnite
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-[#00D4FF] text-[#00D4FF] hover:bg-[#00D4FF] hover:text-black"
                onClick={() => navigate("/mentorias?game=fortnite")}
              >
                <Play className="h-5 w-5 mr-2" />
                Ver Mentorias
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* O que é Fortnite */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              O que é <span className="text-[#00D4FF]">Fortnite</span>?
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Fortnite é um Battle Royale gratuito onde 100 jogadores lutam para ser o último 
              sobrevivente. O diferencial está na mecânica de construção, que adiciona uma 
              camada estratégica única ao gameplay.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="bg-[#181D24] border-[#222A35]">
              <CardHeader>
                <Hammer className="h-8 w-8 text-[#00D4FF] mb-2" />
                <CardTitle className="text-white">Construção Única</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">
                  Sistema de construção em tempo real que permite criar 
                  estruturas defensivas e ofensivas durante o combate.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#181D24] border-[#222A35]">
              <CardHeader>
                <Target className="h-8 w-8 text-[#00D4FF] mb-2" />
                <CardTitle className="text-white">Battle Royale</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">
                  100 jogadores em uma ilha que diminui constantemente, 
                  forçando encontros estratégicos até sobrar apenas um.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#181D24] border-[#222A35]">
              <CardHeader>
                <Gamepad2 className="h-8 w-8 text-[#00D4FF] mb-2" />
                <CardTitle className="text-white">Multiplataforma</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">
                  Jogue com amigos em PC, console e mobile, 
                  com crossplay completo entre todas as plataformas.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-[#181D24]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Perguntas <span className="text-[#00D4FF]">Frequentes</span>
            </h2>
            <p className="text-xl text-gray-300">
              Respostas para as dúvidas mais comuns sobre Fortnite.
            </p>
          </motion.div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-[#0f141c] border-[#222A35]">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300">{faq.answer}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#00D4FF] to-[#00B8E6]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Pronto para Conseguir Victory Royales?
            </h2>
            <p className="text-xl text-black/80 mb-8">
              Acelere seu progresso com mentores profissionais. Aprenda construção avançada, 
              estratégias de end-game e domine o Fortnite competitivo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-black text-[#00D4FF] hover:bg-gray-800"
                onClick={() => navigate("/mentor-fortnite")}
              >
                <Users className="h-5 w-5 mr-2" />
                Encontrar Mentor Agora
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-black text-black hover:bg-black hover:text-[#00D4FF]"
                onClick={() => navigate("/mentorias?game=fortnite")}
              >
                <Trophy className="h-5 w-5 mr-2" />
                Ver Todas as Mentorias
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ComoJogarFortnite; 